/**
 * Posição fixa (`position: fixed`) de um popover ancorado num elemento.
 *
 * <p>Usada pelos popovers de estoque da tela do pedido e pelo tooltip de desenhos do produto
 * (`app-desenhos-produto`). Eles ficam fora da tabela de itens, que
 * rola dentro de um container com `overflow`, e fora do `.card-industrial`, que ganha `transform`
 * no hover — dentro de qualquer um dos dois, o popover seria cortado ou posicionado errado.</p>
 */
export interface PosicaoPopover {
  top: number;
  left: number;
  /** Lado da âncora em que o cartão abriu; a seta aponta para ela. */
  lado: 'abaixo' | 'acima';
  /** Distância da ponta da seta até a borda esquerda do cartão. */
  setaLeft: number;
}

const MARGEM_JANELA = 8;
const DISTANCIA_ANCORA = 10;
const MARGEM_SETA = 18;

/** Abre abaixo quando cabe; senão, do lado com mais espaço. Nunca sai da janela. */
export function calcularPosicaoPopover(
  ancora: { top: number; bottom: number; left: number; width: number },
  cartao: { largura: number; altura: number },
  janela: { largura: number; altura: number }
): PosicaoPopover {
  const espacoAbaixo = janela.altura - ancora.bottom - DISTANCIA_ANCORA - MARGEM_JANELA;
  const espacoAcima = ancora.top - DISTANCIA_ANCORA - MARGEM_JANELA;
  const lado = cartao.altura <= espacoAbaixo || espacoAbaixo >= espacoAcima ? 'abaixo' : 'acima';

  const topIdeal = lado === 'abaixo'
    ? ancora.bottom + DISTANCIA_ANCORA
    : ancora.top - DISTANCIA_ANCORA - cartao.altura;
  const top = limitar(topIdeal, MARGEM_JANELA, janela.altura - cartao.altura - MARGEM_JANELA);

  const centroAncora = ancora.left + ancora.width / 2;
  const left = limitar(
    centroAncora - cartao.largura / 2,
    MARGEM_JANELA,
    janela.largura - cartao.largura - MARGEM_JANELA
  );
  const setaLeft = limitar(centroAncora - left, MARGEM_SETA, cartao.largura - MARGEM_SETA);

  return { top, left, lado, setaLeft };
}

/**
 * Mede o cartão já renderizado e aplica a posição direto no elemento. Não passa pelo change
 * detection, então pode ser chamada no `ngAfterViewInit` sem `ExpressionChangedAfterChecked`.
 * O cartão nasce com `visibility: hidden` e só aparece aqui, já no lugar.
 */
export function posicionarPopover(cartao: HTMLElement, ancora: HTMLElement): void {
  const posicao = calcularPosicaoPopover(
    ancora.getBoundingClientRect(),
    { largura: cartao.offsetWidth, altura: cartao.offsetHeight },
    { largura: window.innerWidth, altura: window.innerHeight }
  );

  cartao.style.top = `${posicao.top}px`;
  cartao.style.left = `${posicao.left}px`;
  cartao.style.setProperty('--seta-left', `${posicao.setaLeft}px`);
  cartao.classList.toggle('lado-acima', posicao.lado === 'acima');
  cartao.classList.toggle('lado-abaixo', posicao.lado === 'abaixo');
  cartao.style.visibility = 'visible';
}

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.max(minimo, Math.min(valor, maximo));
}
